"use client";

import GenreEventLink from "@components/links/genreEventLink";
import LoadingSpinner from "@components/loadingSpinner";
import SearchResults from "@components/searchResults";
import { Heading } from "@components/theming";
import { GenreEventPagination } from "@types";
import flatten from "lodash/flatten";
import { KeyedMutator } from "swr";
import useSWRInfinite from "swr/infinite";
import {
  ExclamationTriangleIcon,
  FaceFrownIcon,
} from "@heroicons/react/24/solid";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import Link from "next/link";
import CityFilter from "./components/cityFilter";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import DateFilter from "./components/dateFilter";
import { BASE_URL, COUNTRY_CODE } from "@utils/constants";
import { useTranslations } from "next-intl";
import { getCookieValue, joinClassNames } from "@utils/helpers";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { genreIds } from "./utils/genreIds";

type PresetType = Parameters<
  ReturnType<typeof useTranslations<"GenrePage.filters.datepresets">>
>[0];

interface Props {
  genreEvents: GenreEventPagination;
  genreId: number;
  genreName: string;
  filters: {
    regions: {
      id: number;
      order: number;
      region: string;
      cities: {
        name: string;
        regionId: number;
      }[];
    }[];
  };
  has_banner: boolean;
}

const PAGE_SIZE = 15;

const getDaysInMonth = (month: number, year: number) =>
  new Date(year, month, 0).getDate();
const getWeekdayForDate = (date: Date) =>
  date.getDay() - 1 === -1 ? 6 : date.getDay() - 1;

const GenresEvents = ({
  genreEvents,
  genreId,
  genreName,
  filters,
  has_banner,
}: Props) => {
  const router = useRouter();
  const pathname = usePathname();
  const params = useSearchParams();
  const cities = params.get("cities");
  const dateFrom = params.get("dateFrom");
  const dateTo = params.get("dateTo");
  const timeZoneOffset = params.get("timeZoneOffset");
  const getKey = (pageIndex: number) => {
    return `data/genres/${genreId}/events?pageSize=${PAGE_SIZE}&pageNumber=${
      pageIndex + 1
    }${cities ? `&cities=${cities}` : ""}${
      dateFrom ? `&dateFrom=${dateFrom}` : ""
    }${dateTo ? `&dateTo=${dateTo}` : ""}${
      timeZoneOffset ? `&timeZoneOffset=${timeZoneOffset}` : ""
    }`;
  };
  const t = useTranslations("GenrePage");
  const tSearch = useTranslations("Search");
  const websiteUid = getCookieValue("itk-website-uid");

  const {
    data: eventData,
    error,
    size,
    setSize,
    isLoading,
  }: {
    data?: Array<GenreEventPagination>;
    error: any;
    mutate: KeyedMutator<any[]>;
    isLoading: boolean;
    isValidating: boolean;
    size: number;
    setSize: (
      size: number | ((_size: number) => number)
    ) => Promise<any[] | undefined>;
  } = useSWRInfinite(
    getKey,
    async (url: string) =>
      fetch(`${BASE_URL}/legacy/${COUNTRY_CODE}/shop/${url}`, {
        headers: [["itk-website-uid", websiteUid || ""]],
      })
        .then(async (res) => {
          if (!res.ok) return [];
          return await res.json();
        })
        .catch(() => {
          return [];
        }),
    { fallbackData: [genreEvents] }
  );

  const flattenEventData = flatten(
    eventData?.map((event) => event.data)
  ).filter((event) => event !== undefined && event !== null);

  let content = <></>;

  if (isLoading) {
    content = (
      <div className="flex justify-center w-full mt-8">
        <LoadingSpinner size="62" />
      </div>
    );
  }

  if (
    !isLoading &&
    (flattenEventData === undefined || flattenEventData === null)
  ) {
    content = (
      <div className="mt-8 pt-8">
        <div className="flex flex-col items-center justify-center">
          <div className="relative">
            <h3 className="relative z-10 font-black text-4xl text-black text-center">
              {t("error.ohno")}
            </h3>
            <ExclamationTriangleIcon className="absolute -top-14 sm:-top-16 -left-16 text-red-400 w-24 h-24 -rotate-12" />
          </div>
          <div className="relative">
            <p className="relative z-10 font-bold text-2xl text-black text-center">
              {t("error.noeventsforgenre", {
                genre:
                  genreId === genreIds["whatson"].id
                    ? ""
                    : genreName.toLowerCase(),
              })}
            </p>
            <FaceFrownIcon className="absolute -bottom-8 -right-6 sm:-right-[4.25rem] text-[#F9CF47] w-24 h-24 rotate-[15deg]" />
          </div>
        </div>
        <p className="mt-8 text-black sm:text-center">
          {t("error.needticketing")}{" "}
          <Link
            className="text-blue-500 visited:text-purple-500"
            href="/contact"
          >
            {t("error.getintouch")}
          </Link>
        </p>
      </div>
    );
  }

  if (!isLoading && flattenEventData.length === 0) {
    content = (
      <div className="mt-8 pt-8">
        <div className="flex flex-col items-center justify-center">
          <div className="relative">
            <h3 className="relative z-10 font-black text-4xl text-black text-center">
              {t("error.ohno")}
            </h3>
            <ExclamationTriangleIcon className="absolute -top-14 sm:-top-16 -left-16 text-red-400 w-24 h-24 -rotate-12" />
          </div>
          <div className="relative">
            <p className="relative z-10 font-bold text-2xl text-black text-center">
              {t("error.noeventsforfilter", {
                genre:
                  genreId === genreIds["whatson"].id
                    ? ""
                    : genreName.toLowerCase(),
              })}
            </p>
            <FaceFrownIcon className="absolute -bottom-8 -right-6 sm:-right-[4.25rem] text-[#F9CF47] w-24 h-24 rotate-[15deg]" />
          </div>
        </div>
        <p className="mt-8 text-black sm:text-center">
          {t("error.tryanotherfilter", {
            genre:
              genreId === genreIds["whatson"].id ? "" : genreName.toLowerCase(),
          })}
        </p>
      </div>
    );
  }

  if (!isLoading && flattenEventData.length > 0) {
    content = (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-8 mt-4 sm:mt-8">
        {flattenEventData.map((event, index) => (
          <GenreEventLink
            key={`${event.name}-${event.venue.name}-${event.venue.location}`}
            event={event}
            imageIsPriority={index < 10}
          />
        ))}
        {flattenEventData?.length % PAGE_SIZE === 0 &&
        !(
          flattenEventData.length ===
          (eventData && eventData[0].pagination.total)
        ) ? (
          <div className="flex justify-center w-full col-span-full">
            <button
              className="flex items-center justify-center gap-2 px-2 py-1 w-fit bg-indigo-100 text-indigo-800 hover:bg-indigo-500 hover:text-white disabled:bg-indigo-200 disabled:text-white disabled:cursor-not-allowed font-semibold rounded-lg transition-colors ease-in-out duration-200"
              disabled={
                (!eventData && !error) ||
                (size > 0 &&
                  !!eventData &&
                  typeof eventData[size - 1] === "undefined")
              }
              onClick={() => setSize((_size) => _size + 1)}
            >
              {(!eventData && !error) ||
              (size > 0 &&
                !!eventData &&
                typeof eventData[size - 1] === "undefined") ? (
                <>
                  <LoadingSpinner
                    size="16"
                    color={
                      (!eventData && !error) ||
                      (size > 0 &&
                        !!eventData &&
                        typeof eventData[size - 1] === "undefined")
                        ? "#ffffff"
                        : "#3730a3"
                    }
                  />
                  <span>{t("loading")}</span>
                </>
              ) : (
                t("loadmore")
              )}
            </button>
          </div>
        ) : (
          <></>
        )}
      </div>
    );
  }

  function calculateDatesForPreset(
    preset: PresetType
  ): [string | null, string | null] {
    const date = format(new Date(), "yyyy-MM-dd");
    const [year, month, day] = date.split("-").map((v) => parseInt(v));
    const date1 = dateFrom || date;
    const date2 = dateTo || date;

    switch (preset) {
      case "today":
        return [date, date];
      case "tomorrow": {
        let [new_year, new_month, new_day] = [year, month, day + 1];
        if (getDaysInMonth(month, year) < new_day) {
          new_day = new_day - getDaysInMonth(month, year);
          new_month = month + 1;
        }
        if (new_month > 12) {
          new_month = 1;
          new_year = year + 1;
        }
        const new_date = `${new_year}-${new_month
          .toString()
          .padStart(2, "0")}-${new_day.toString().padStart(2, "0")}`;
        return [new_date, new_date];
      }
      case "weekend": {
        const distance_to_saturday = 5 - getWeekdayForDate(new Date(date));
        let [sat_year, sat_month, sat_day] = [
          year,
          month,
          day + distance_to_saturday,
        ];
        let [sun_year, sun_month, sun_day] = [
          year,
          month,
          day + distance_to_saturday + 1,
        ];

        if (getDaysInMonth(month, year) < sat_day) {
          sat_day = sat_day - getDaysInMonth(month, year);
          sat_month = month + 1;
        }
        if (sat_month > 12) {
          sat_month = 1;
          sat_year = year + 1;
        }
        if (getDaysInMonth(month, year) < sun_day) {
          sun_day = sun_day - getDaysInMonth(month, year);
          sun_month = month + 1;
        }
        if (sun_month > 12) {
          sun_month = 1;
          sun_year = year + 1;
        }

        const sat_date = `${sat_year}-${sat_month
          .toString()
          .padStart(2, "0")}-${sat_day.toString().padStart(2, "0")}`;
        const sun_date = `${sun_year}-${sun_month
          .toString()
          .padStart(2, "0")}-${sun_day.toString().padStart(2, "0")}`;
        return [sat_date, sun_date];
      }
      case "week": {
        let [new_year, new_month, new_day] = [year, month, day + 6];
        if (getDaysInMonth(month, year) < new_day) {
          new_day = new_day - getDaysInMonth(month, year);
          new_month = month + 1;
        }
        if (new_month > 12) {
          new_month = 1;
          new_year = year + 1;
        }
        const new_date = `${new_year}-${new_month
          .toString()
          .padStart(2, "0")}-${new_day.toString().padStart(2, "0")}`;
        return [date, new_date];
      }
      case "custom": {
        return [date1, date2];
      }
      default: {
        return [null, null];
      }
    }
  }

  function isSelectedPreset(preset_to_check: PresetType) {
    const [date1, date2] = calculateDatesForPreset(preset_to_check);

    return dateFrom === date1 && dateTo === date2;
  }

  const no_preset_selected = !dateFrom && !dateTo;

  return (
    <div className="relative">
      <Heading
        as="h2"
        size={{ initial: "7", sm: "8" }}
        weight="bold"
        shopsiteClasses="relative z-10 text-3xl sm:text-5xl font-black text-black w-fit"
      >
        {genreName}
      </Heading>
      {genreId === genreIds["whatson"].id && (
        <div className="mt-4 lg:hidden">
          <SearchResults
            icon={
              <span className="flex items-center justify-start gap-1 px-4 py-2 text-gray-600 bg-white hover:bg-gray-100 border border-gray-300 rounded-md transition-colors ease-in-out duration-300 cursor-default w-full">
                <MagnifyingGlassIcon className="h-5 w-5" />
                <span className="font-semibold">{tSearch("searchevents")}</span>
              </span>
            }
          />
        </div>
      )}
      <div
        className={joinClassNames(
          "md:sticky z-20 mt-4 right-0 flex flex-row items-center gap-4 w-fit flex-wrap bg-white/60 border border-gray-300/60 backdrop-blur rounded-xl p-2",
          has_banner ? "top-[150px]" : "top-[100px]"
        )}
      >
        <motion.button
          whileTap={!no_preset_selected ? { scale: 0.95 } : undefined}
          onClick={() => {
            const current_params = new URLSearchParams(
              Array.from(params.entries())
            );
            current_params.delete("dateFrom");
            current_params.delete("dateTo");
            current_params.delete("timeZoneOffset");
            const query = current_params.toString();

            router.push(`${pathname}${query ? `?${query}` : ""}`);
          }}
          className={joinClassNames(
            "relative group flex items-center justify-center rounded h-7 focus:outline-none transition-colors ease-in-out duration-200",
            !no_preset_selected ? "hover:bg-indigo-50" : ""
          )}
        >
          <span
            className={joinClassNames(
              "z-20 font-medium h-6 px-1 transition-colors ease-in-out duration-200",
              no_preset_selected
                ? "text-indigo-800"
                : "text-gray-900 group-hover:text-indigo-800"
            )}
          >
            {t("filters.datepresets.allevents")}
          </span>
          {no_preset_selected && (
            <motion.span
              layoutId={`genre_events_highlight_${genreId}`}
              className="absolute z-10 h-full w-full rounded bg-indigo-100"
            />
          )}
        </motion.button>
        <motion.button
          whileTap={!isSelectedPreset("today") ? { scale: 0.95 } : undefined}
          onClick={() => {
            const [date1, date2] = calculateDatesForPreset("today");
            if (date1 && date2) {
              const current_params = new URLSearchParams(
                Array.from(params.entries())
              );
              const timezone_offset = (
                new Date().getTimezoneOffset() * -1
              ).toString();

              current_params.set("dateFrom", date1);
              current_params.set("dateTo", date2);
              current_params.set("timeZoneOffset", timezone_offset);
              const query = current_params.toString();

              router.push(`${pathname}${query ? `?${query}` : ""}`);
            }
          }}
          className={joinClassNames(
            "relative group flex items-center justify-center rounded h-7 focus:outline-none transition-colors ease-in-out duration-200",
            !isSelectedPreset("today") ? "hover:bg-indigo-50" : ""
          )}
        >
          <span
            className={joinClassNames(
              "z-20 font-medium h-6 px-1 transition-colors ease-in-out duration-200",
              isSelectedPreset("today")
                ? "text-indigo-800"
                : "text-gray-900 group-hover:text-indigo-800"
            )}
          >
            {t("filters.datepresets.today")}
          </span>
          {isSelectedPreset("today") && (
            <motion.span
              layoutId={`genre_events_highlight_${genreId}`}
              className="absolute z-10 h-full w-full rounded bg-indigo-100"
            />
          )}
        </motion.button>
        <motion.button
          whileTap={!isSelectedPreset("tomorrow") ? { scale: 0.95 } : undefined}
          onClick={() => {
            const [date1, date2] = calculateDatesForPreset("tomorrow");
            if (date1 && date2) {
              const current_params = new URLSearchParams(
                Array.from(params.entries())
              );
              const timezone_offset = (
                new Date().getTimezoneOffset() * -1
              ).toString();

              current_params.set("dateFrom", date1);
              current_params.set("dateTo", date2);
              current_params.set("timeZoneOffset", timezone_offset);
              const query = current_params.toString();

              router.push(`${pathname}${query ? `?${query}` : ""}`);
            }
          }}
          className={joinClassNames(
            "relative group flex items-center justify-center rounded h-7 focus:outline-none transition-colors ease-in-out duration-200",
            !isSelectedPreset("tomorrow") ? "hover:bg-indigo-50" : ""
          )}
        >
          <span
            className={joinClassNames(
              "z-20 font-medium h-6 px-1 transition-colors ease-in-out duration-200",
              isSelectedPreset("tomorrow")
                ? "text-indigo-800"
                : "text-gray-900 group-hover:text-indigo-800"
            )}
          >
            {t("filters.datepresets.tomorrow")}
          </span>
          {isSelectedPreset("tomorrow") && (
            <motion.span
              layoutId={`genre_events_highlight_${genreId}`}
              className="absolute z-10 h-full w-full rounded bg-indigo-100"
            />
          )}
        </motion.button>
        <motion.button
          whileTap={!isSelectedPreset("weekend") ? { scale: 0.95 } : undefined}
          onClick={() => {
            const [date1, date2] = calculateDatesForPreset("weekend");
            if (date1 && date2) {
              const current_params = new URLSearchParams(
                Array.from(params.entries())
              );
              const timezone_offset = (
                new Date().getTimezoneOffset() * -1
              ).toString();

              current_params.set("dateFrom", date1);
              current_params.set("dateTo", date2);
              current_params.set("timeZoneOffset", timezone_offset);
              const query = current_params.toString();

              router.push(`${pathname}${query ? `?${query}` : ""}`);
            }
          }}
          className={joinClassNames(
            "relative group flex items-center justify-center rounded h-7 focus:outline-none transition-colors ease-in-out duration-200",
            !isSelectedPreset("weekend") ? "hover:bg-indigo-50" : ""
          )}
        >
          <span
            className={joinClassNames(
              "z-20 font-medium h-6 px-1 transition-colors ease-in-out duration-200",
              isSelectedPreset("weekend")
                ? "text-indigo-800"
                : "text-gray-900 group-hover:text-indigo-800"
            )}
          >
            {t("filters.datepresets.weekend")}
          </span>
          {isSelectedPreset("weekend") && (
            <motion.span
              layoutId={`genre_events_highlight_${genreId}`}
              className="absolute z-10 h-full w-full rounded bg-indigo-100"
            />
          )}
        </motion.button>
        <motion.button
          whileTap={!isSelectedPreset("week") ? { scale: 0.95 } : undefined}
          onClick={() => {
            const [date1, date2] = calculateDatesForPreset("week");
            if (date1 && date2) {
              const current_params = new URLSearchParams(
                Array.from(params.entries())
              );
              const timezone_offset = (
                new Date().getTimezoneOffset() * -1
              ).toString();

              current_params.set("dateFrom", date1);
              current_params.set("dateTo", date2);
              current_params.set("timeZoneOffset", timezone_offset);
              const query = current_params.toString();

              router.push(`${pathname}${query ? `?${query}` : ""}`);
            }
          }}
          className={joinClassNames(
            "relative group flex items-center justify-center rounded h-7 focus:outline-none transition-colors ease-in-out duration-200",
            !isSelectedPreset("week") ? "hover:bg-indigo-50" : ""
          )}
        >
          <span
            className={joinClassNames(
              "z-20 font-medium h-6 px-1 transition-colors ease-in-out duration-200",
              isSelectedPreset("week")
                ? "text-indigo-800"
                : "text-gray-900 group-hover:text-indigo-800"
            )}
          >
            {t("filters.datepresets.week")}
          </span>
          {isSelectedPreset("week") && (
            <motion.span
              layoutId={`genre_events_highlight_${genreId}`}
              className="absolute z-10 h-full w-full rounded bg-indigo-100"
            />
          )}
        </motion.button>
        <div className="h-7 border-l border-gray-300" />
        <CityFilter regions={filters.regions} />
        <DateFilter />
      </div>
      {content}
    </div>
  );
};

export default GenresEvents;
